import { SAVED_FILTER_KEYS } from './saveFilters';

const AUTH_STORAGE_KEYS = ['token', 'id', 'id_setor', 'name', 'role', 'setor', 'username', 'permissoes'];

export const AUTH_CHANGED_EVENT = 'auth-changed';

export function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const [, payloadB64] = token.split('.');
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getToken() {
  const fromStorage = localStorage.getItem('token');
  if (fromStorage) return fromStorage;
  try {
    const user = sessionStorage.getItem('user');
    if (!user) return '';
    return JSON.parse(user).token || '';
  } catch {
    return '';
  }
}

function parsePermissoes(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getStoredUser() {
  const token = getToken();
  const payload = decodeJwtPayload(token);
  const permissoes = parsePermissoes(localStorage.getItem('permissoes'));

  return {
    token,
    id: localStorage.getItem('id') || payload?.id || payload?.sub || '',           
    id_setor: localStorage.getItem('id_setor') || payload?.id_setor || '',         
    nome: localStorage.getItem('name') || payload?.nome || '',
    username: localStorage.getItem('username') || payload?.username || '',
    perfil: localStorage.getItem('role') || payload?.perfil_nome || payload?.profile || '',
    setor: localStorage.getItem('setor') || payload?.setor_sigla || '',
    permissoes: permissoes.length ? permissoes : payload?.permissoes || [],
  };
}

export function hasPermission(slug, user = getStoredUser()) {
  if (!slug) return false;
  return Array.isArray(user?.permissoes) && user.permissoes.includes(slug);
}

export function hasRole(roleName, user = getStoredUser()) {
  return (user?.perfil || '').toUpperCase().trim() === (roleName || '').toUpperCase().trim();
}

export function isAdminUser(user = getStoredUser()) {
  return hasPermission('gerenciar_usuarios', user) || hasRole('ADMINISTRADOR', user);
}

export function isEditorUser(user = getStoredUser()) {
  return hasRole('EDITOR', user) || hasRole('EDITOR_PROCESSOS', user);
}

export function isGerenteUser(user = getStoredUser()) {
  return hasPermission('acessar_arquivo_morto', user) || hasRole('GERENTE', user);
}

export function normalizeMeResponse(data, fallbackUsername = '') {
  if (!data || typeof data !== 'object') {
    return {
      id: '',                    
      id_setor: '',              
      nome: fallbackUsername,
      username: fallbackUsername,
      perfil: '',
      setor: '',
      permissoes: [],
    };
  }

  return {
    id: data.id || '',                                                             
    id_setor: data.id_setor || '',                                                 
    nome: data.nome || data.name || fallbackUsername,
    username: data.username || fallbackUsername,
    perfil: data.perfil_nome || data.profile || data.perfil || '',
    setor: data.setor_sigla || data.setor || '',
    permissoes: Array.isArray(data.permissoes) ? data.permissoes : [],
  };
}

export function userFromJwt(token, fallbackUsername = '') {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return {
      id: '',
      id_setor: '',
      nome: fallbackUsername,
      username: fallbackUsername,
      perfil: '',
      setor: '',
      permissoes: [],
    };
  }

  return {
    id: payload.id || payload.sub || '',
    id_setor: payload.id_setor || '',
    nome: payload.nome || fallbackUsername,
    username: payload.username || fallbackUsername,
    perfil: payload.perfil_nome || payload.profile || '',
    setor: payload.setor_sigla || '',
    permissoes: Array.isArray(payload.permissoes) ? payload.permissoes : [],
  };
}

export async function fetchUsuarioMe(token = getToken()) {
  const response = await fetch('/api/usuarios/me', {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar usuário (${response.status})`);
  }

  const data = await response.json();
  return normalizeMeResponse(data, decodeJwtPayload(token)?.username || '');
}

export async function resolveUserAfterLogin(token, loginUsername) {
  let user = userFromJwt(token, loginUsername);

  try {
    const me = await fetchUsuarioMe(token);
    user = { ...user, ...me };
  } catch (err) {
    console.warn('Erro ao buscar /api/usuarios/me:', err);
  }

  if (!user.username) user.username = loginUsername;
  if (!user.nome) user.nome = loginUsername;

  persistUserSession(token, user);
  return user;
}

export function persistUserSession(token, user) {
  localStorage.setItem('token', token);
  localStorage.setItem('id', user.id || '');                                       
  localStorage.setItem('id_setor', user.id_setor || '');                           
  localStorage.setItem('name', user.nome || '');
  localStorage.setItem('role', user.perfil || '');
  localStorage.setItem('setor', user.setor || '');
  localStorage.setItem('username', user.username || '');
  localStorage.setItem('permissoes', JSON.stringify(user.permissoes || []));

  sessionStorage.setItem(
    'user',
    JSON.stringify({ loggedInUser: true, token })
  );

  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
  window.dispatchEvent(new CustomEvent('contracts-auth-changed'));
}

export function clearAuthSession() {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  SAVED_FILTER_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem('user');
  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
  window.dispatchEvent(new CustomEvent('contracts-auth-changed'));
}

export function isTokenExpired(token = getToken()) {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  return payload.exp <= Math.floor(Date.now() / 1000);
}

const SENHA_CLASSES = ['ABCDEFGHJKMNPQRSTUVWXYZ', 'abcdefghjkmnpqrstuvwxyz', '23456789', '@#$%*'];

function indiceAleatorio(max) {
  const limite = Math.floor(0x100000000 / max) * max;
  const buffer = new Uint32Array(1);
  do {
    window.crypto.getRandomValues(buffer);
  } while (buffer[0] >= limite);
  return buffer[0] % max;
}

export function gerarSenhaAleatoria(tamanho = 14) {
  const total = Math.max(12, tamanho);
  const todos = SENHA_CLASSES.join('');
  const chars = SENHA_CLASSES.map((classe) => classe[indiceAleatorio(classe.length)]);
  while (chars.length < total) chars.push(todos[indiceAleatorio(todos.length)]);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = indiceAleatorio(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

export function getAuthHeaders(token = getToken()) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function readProcessosAuth() {
  const user = getStoredUser();
  const roleRaw = (user.perfil || '').toUpperCase().trim();

  return {
    token: user.token,
    roleRaw,
    setor: user.setor,
    name: user.nome,
    username: user.username,
    permissoes: user.permissoes,
    isAdmin: isAdminUser(user),
    isEditor: isEditorUser(user),
    isGerente: isGerenteUser(user),
  };
}